import React, { useState, useRef } from 'react'
import {
  Box, Button, ButtonGroup, MenuItem, MenuDivider, MenuButton, Menu, MenuList,
  useSteps, Stepper, Step, StepIndicator, StepStatus, StepSeparator, StepTitle
} from '@chakra-ui/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowsUpDown, faLocationDot } from '@fortawesome/free-solid-svg-icons'
import { ConfigMenuNumberInput, OSTooltip } from '../configmenus/MenuCommon'
import { Class_ApplicationData } from '../../types/ApplicationData'
import { Class_DataTagGroup } from '../../types/TagGroup'

/**
 * Right toolbar for some simple functionnality on the DA (Draw flow, recenter DA,...)
 *
 * @param {*} {
 *   new_data,
 * }
 * @return {*}
 */
export const ToolBarBottom = ({ new_data }: { new_data: Class_ApplicationData }) => {
  const { t } = new_data

  // Local State to update this & subcomponent
  const [, setUpdater] = useState(0)
  const refreshThis = () => {
    setUpdater(a => a + 1)
  }
  new_data.menu_configuration.ref_to_toolbar_bottom_updater.current = refreshThis

  let btn_mouse_mode_edition = <></>
  if (!new_data.is_static) {
    btn_mouse_mode_edition = <ComponentMouseMode app_data={new_data} updateParentComponent={refreshThis} />
  }
  const sizeBottomMenu = document.getElementsByClassName('BottomMenu')[0]?.getBoundingClientRect().height ?? 0
  return <Box
    layerStyle={new_data.is_static ? 'toolbar_right' : 'toolbar_bottom'} // Changement du layerStyle
    bottom={new_data.is_static ? '' : 'calc(' + String(sizeBottomMenu + (new_data.drawing_area.fit_margin / 2)) + 'px + 1rem)'}
  >
    {btn_mouse_mode_edition}
    {!new_data.is_static ? <ComponentPositionMode
      app_data={new_data}
      updateParentComponent={refreshThis}
    /> : <></>}
    <ComponetStretchButtons
      app_data={new_data}
      updateParentComponent={refreshThis}
    />
    {!new_data.is_static ? <OSTooltip
      placement='top'
      label={t('Banner.tooltipHelp')}
      isAlwaysOpen={!new_data.is_static && new_data.menu_configuration.show_splashscreen}
    >
      <Button
        className='toolbar_bottom_help'
        variant='info'
        size='sizeToolbarButton'
        onClick={() => new_data.menu_configuration.dict_setter_show_dialog.ref_setter_show_modal_welcome.current!(true)}
      >
        ?
      </Button>
    </OSTooltip> : <></>
    }
  </Box>
}

const ComponentMouseMode = (
  { app_data, updateParentComponent }: { app_data: Class_ApplicationData, updateParentComponent: () => void }) => {
  const { t, menu_configuration, drawing_area, icon_library } = app_data
  const size = app_data.is_static ? 'sizeToolbarButtonStatic' : 'sizeToolbarButton'
  { /* Boutons permettant soit de passer la souris en mode sélection soit en mode création noeud/flux */ }
  return <OSTooltip
    placement={app_data.is_static ? 'left' : 'top'}
    label={t('Banner.tooltipLiason')}
    isAlwaysOpen={menu_configuration.show_splashscreen}
  >
    <ButtonGroup className='toolbar_bottom_mouse_mode' isAttached orientation={app_data.is_static ? 'vertical' : 'horizontal'}> {/* Orientation verticale */}
      <Button
        variant={drawing_area.isInEditionMode() ? 'toolbar_button_mouse_mode_activated' : 'toolbar_button_mouse_mode'}
        id='button_selection_edition'
        size={size}
        onClick={() => {
          if (!drawing_area.isInEditionMode()) {
            drawing_area.switchMode()
            updateParentComponent()
          }
        }}>
        {icon_library.icon_DA_edit}
      </Button>
      <Button
        variant={drawing_area.isInSelectionMode() ? 'toolbar_button_mouse_mode_activated' : 'toolbar_button_mouse_mode'}
        id='button_selection_edition'
        size={size}
        onClick={() => {
          if (!drawing_area.isInSelectionMode()) {
            drawing_area.switchMode()
            updateParentComponent()
          }
        }}>
        {icon_library.icon_DA_selection}
      </Button>
      <Button
        variant={drawing_area.isInStylePaintMode() ? 'toolbar_button_mouse_mode_activated' : 'toolbar_button_mouse_mode'}
        isDisabled={!drawing_area.isInStylePaintMode() && drawing_area.selected_elements_list.length !== 1}
        size={size}
        onClick={() => {
          if (drawing_area.isInStylePaintMode()) {
            drawing_area.exitStylePaintMode()
          } else {
            const selected = drawing_area.selected_elements_list[0]
            if (selected) drawing_area.enterStylePaintMode(selected)
          }
          updateParentComponent()
        }}>
        {icon_library.icon_style_paint}
      </Button>
    </ButtonGroup>
  </OSTooltip>
}


const ComponentPositionMode = ({ app_data, updateParentComponent }: { app_data: Class_ApplicationData, updateParentComponent: () => void }) => {
  const { t, drawing_area } = app_data
  const size = app_data.is_static ? 'sizeToolbarButtonStatic' : 'sizeToolbarButton'
  const isParametric = drawing_area.sankey.styles_dict['default'].shape_position_type === 'parametric'
  return <ButtonGroup className='toolbar_bottom_position_mode' isAttached orientation={app_data.is_static ? 'vertical' : 'horizontal'}>
    <OSTooltip placement='top' label={t('Banner.posMode_parametric')}>
      <Button
        variant={isParametric ? 'toolbar_button_mouse_mode_activated' : 'toolbar_button_mouse_mode'}
        size={size}
        onClick={() => {
          if (!isParametric) { drawing_area.setParametricMode(); updateParentComponent() }
        }}>
        <FontAwesomeIcon icon={faArrowsUpDown} />
      </Button>
    </OSTooltip>
    <OSTooltip placement='top' label={t('Banner.posMode_absolute')}>
      <Button
        variant={!isParametric ? 'toolbar_button_mouse_mode_activated' : 'toolbar_button_mouse_mode'}
        size={size}
        onClick={() => {
          if (isParametric) { drawing_area.setAbsoluteMode(); updateParentComponent() }
        }}>
        <FontAwesomeIcon icon={faLocationDot} />
      </Button>
    </OSTooltip>
  </ButtonGroup>
}

/**
 *Buttons component to recenter DA horizontally or vertically
 *
 * @param {*} { new_data, updateParentComponent }
 * @return {*}
 */
const ComponetStretchButtons = ({ app_data, updateParentComponent }: { app_data: Class_ApplicationData, updateParentComponent: () => void }) => {
  // Use variable from class
  const { t } = app_data
  const size = app_data.is_static ? 'sizeToolbarButtonStatic' : 'sizeToolbarButton'
  const logo_btn_fs = document.fullscreenElement ? app_data.icon_library.icon_enter_fullscreen : app_data.icon_library.icon_exit_fullscreen

  // const tmp = new KeyboardEvent('keydown', { key: 'F', ctrlKey: true })
  // const doc = document
  // // Function that trigger event on Ctrl + F
  const executeManualCtrlF = async () => {
    //evt.preventDefault()
    // Toggle fullscreen
    if (!document.fullscreenElement) {
      await document.documentElement.requestFullscreen()
      app_data.drawing_area.draw()
    }
    else if (document.exitFullscreen) {
      await document.exitFullscreen()
      app_data.drawing_area.draw()
    }
    updateParentComponent()
  }

  return <ButtonGroup className='toolbar_bottom_stretch' isAttached orientation={app_data.is_static ? 'vertical' : 'horizontal'}>
    <OSTooltip placement={app_data.is_static ? 'left' : 'top'} label={t('Banner.tooltipAdjustH')}>
      <Button variant='toolbar_button_6'
        size={size}
        onClick={() => app_data.drawing_area.areaAutoFit(true)}>
        {app_data.icon_library.icon_area_fit_horiz}
      </Button>
    </OSTooltip>
    <OSTooltip placement={app_data.is_static ? 'left' : 'top'} label={t('Banner.tooltipAdjustV')}>
      <Button variant='toolbar_button_6'
        size={size}
        onClick={() => app_data.drawing_area.areaAutoFit(false)}>
        {app_data.icon_library.icon_area_fit_vert}
      </Button>
    </OSTooltip>

    <OSTooltip
      placement={app_data.is_static ? 'left' : 'top'}
      label={document.fullscreenElement ? t('Banner.quit_fullscreen') : t('Banner.fullscreen')}
    >
      <Button
        variant='toolbar_button_6'
        id='button_fullscreen'
        size={size}
        onClick={executeManualCtrlF}
      >
        {logo_btn_fs}
      </Button>
    </OSTooltip>
  </ButtonGroup>
}

export const DrawerSequenceDataTagg = ({ new_data }: { new_data: Class_ApplicationData }) => {
  const { icon_library } = new_data
  const { icon_repeat_sequence, icon_play, icon_pause, icon_activated, icon_open_selector } = icon_library
  const [, setUpdate] = useState(0)
  new_data.menu_configuration.ref_to_drawer_sequence_data_tag_updater.current = () => setUpdate(a => a + 1)
  const [active_grp, setActiveGrp] = useState('')

  const list_grp_seq = new_data.drawing_area.sankey.getTagGroupsAsList('data_taggs').filter(grp => (grp as Class_DataTagGroup).banner == 'sequence')
  const dict_data_grp = new_data.drawing_area.sankey.getTagGroupsAsDict('data_taggs')
  const list_grp_seq_id = list_grp_seq.map(grp => grp.id)
  const has_sequence = list_grp_seq.length > 0

  if (has_sequence && !list_grp_seq_id.includes(active_grp)) {
    setActiveGrp(list_grp_seq_id[0])
  }
  const ref_set_number_input = useRef((_: string | null | undefined) => null)
  ref_set_number_input.current(String(new_data.menu_configuration.timeout_sequence))

  // Create stepper of active groupe
  const stepper_sequence: JSX.Element = <StepperDataTagg new_data={new_data} DataGroup={dict_data_grp[active_grp] as Class_DataTagGroup} />

  // Logo of the button to start/pause the sequence
  const logo_btn = !new_data.menu_configuration.is_playing_sequence ? icon_play : icon_pause
  const setter_timeout = <Box layerStyle='config_timeout_sequence' >
    <Box layerStyle='menuconfigpanel_option_name'>
      {new_data.t('Tags.sequence_timeout')}
    </Box>

    <ConfigMenuNumberInput
      t={new_data.t}
      default_value={new_data.menu_configuration.timeout_sequence}
      minimum_value={1}
      function_on_blur={(value) => {
        if (value) {
          if (value > 0) {
            new_data.menu_configuration.timeout_sequence = value
          }
        }
      }}
      unit_text='ms'
    />
  </Box>

  // If multiple dataTagg are a sequence we can add a Menu to choose which one we want to launch
  const select_active_grp = list_grp_seq.length > 1 ? <>
    {list_grp_seq.map((el, idx) => {
      return <MenuItem
        key={'select_grp_seq_' + idx}
        onClick={() => setActiveGrp(el.id)}
        icon={active_grp === el.id ? icon_activated : <></>}
        style={{ display: 'block' }}
      >
        {el.name}
      </MenuItem>
    })}
    <MenuDivider />
  </> : <></>

  // Menu with option like selective active sequence & timeout between steps
  const option_btn = <Menu>
    <MenuButton
      as={Button}
      isDisabled={new_data.menu_configuration.is_playing_sequence}
      variant={new_data.menu_configuration.is_playing_sequence ? 'button_dataTagg_sequence_menu_play' : 'button_dataTagg_sequence_menu_pause'}
    >
      {icon_open_selector}
    </MenuButton>
    <MenuList>
      {select_active_grp}
      {setter_timeout}
    </MenuList>
  </Menu>

  return has_sequence ? (
    <Box
      layerStyle='box_sequence'
    >
      <ButtonGroup isAttached>
        <Button
          variant={new_data.menu_configuration.is_playing_sequence ? 'button_dataTagg_sequence_play' : 'button_dataTagg_sequence_pause'}
          onClick={() => {
            // Either launch or stop data sequence
            if (new_data.menu_configuration.is_playing_sequence) {
              // Stop sequence
              new_data.menu_configuration.is_playing_sequence = false
            } else {
              // Start sequence
              new_data.menu_configuration.is_playing_sequence = true
              const curr_active_grp = new_data.drawing_area.sankey.getTagGroupsAsDict('data_taggs')[active_grp] as Class_DataTagGroup
              new_data.menu_configuration.launchDataSequence(curr_active_grp)
            }
            setUpdate(a => a + 1)
          }}
        >
          {logo_btn}
        </Button>
        <Button
          variant={new_data.menu_configuration.is_sequence_loop ? 'button_dataTagg_sequence_play' : 'button_dataTagg_sequence_pause'}
          onClick={() => {
            // Switch 'is sequence loop' value
            new_data.menu_configuration.is_sequence_loop = !new_data.menu_configuration.is_sequence_loop
            setUpdate(a => a + 1)
          }}>
          {icon_repeat_sequence}
        </Button>
        {option_btn}
      </ButtonGroup>
      {stepper_sequence}
    </Box>
  ) : <></>
}

// Compoenent returing a stepper of a dataTagg where each step is a tag of the group with visual indication to which tag is selected
const StepperDataTagg = ({ new_data, DataGroup }: { new_data: Class_ApplicationData, DataGroup: Class_DataTagGroup }) => {
  const stepper_sequence = DataGroup.tags_list.map((tag, idx) => { return { id_tag: tag.id, title: tag.name, selected: tag.is_selected, id: idx } })
  const selected_id = stepper_sequence.find(el => el.selected)?.id ?? -1
  const { activeStep, setActiveStep } = useSteps({
    index: selected_id,
    count: stepper_sequence.length,
  })

  if (activeStep !== -1 && activeStep !== selected_id) {
    setActiveStep(selected_id)
  }
  // Fucntion used when we click on a step to manually switch to clicked tag
  const switchCurrTag = (idx: number) => {
    DataGroup.selectTagsFromId(stepper_sequence[idx].id_tag)
    new_data.drawing_area.areaAutoFit()
    new_data.menu_configuration.updateAllComponentsRelatedToDataTags()
  }

  return <Box layerStyle='box_stepper'>
    {/* First stepper that have progression bar of the sequence with steps */}
    <Stepper index={activeStep} size={'sm'} variant='sequenceStepper'>
      {stepper_sequence.map((step, index) => (
        <Step key={index} onClick={() => switchCurrTag(index)}>
          <>
            <Box width='100%'>
              <Box display='flex' alignItems='center'>
                <StepIndicator
                  sx={{
                    '[data-status=complete] &': {
                      background: 'white',
                      borderWidth: '2px',
                      borderColor: 'secondaire.3',
                    },
                    '[data-status=active] &': {
                      background: 'primaire.3',
                      borderColor: 'secondaire.3',
                    },
                    '[data-status=incomplete] &': {
                      background: 'white',
                      borderColor: 'secondaire.3',
                    },
                  }}
                >
                  <StepStatus />

                </StepIndicator>

                <StepSeparator
                  sx={{
                    '[data-status=complete] &': {
                      background: 'lightgrey',
                    },
                    '[data-status=active] &': {
                      background: 'lightgrey',
                    },
                    '[data-status=incomplete] &': {
                      background: 'lightgrey',
                    },
                  }} />
              </Box>

            </Box>
          </>
        </Step>
      ))}
    </Stepper>

    {/* Second stepper just to have text well aligned with indicator */}
    <Stepper index={activeStep} size={'sm'} variant='sequenceStepper'>
      {stepper_sequence.map((step, index) => (
        <Step key={index} onClick={() => switchCurrTag(index)}>
          <>
            <Box width='100%'>
              <Box display='flex' alignItems='center'>

                <StepTitle >{step.title}</StepTitle>
              </Box>

            </Box>
          </>

        </Step>

      ))}
    </Stepper>
  </Box>
}